import React, { useEffect, useState } from "react";
import "../../Styles/invoiceTable.css";
import html2pdf from "html2pdf.js";
import TablePagination from "@mui/material/TablePagination";
import ReLoginModal from "../../components/ReLoginModal";
import { BsSearch } from "react-icons/bs";
import * as XLSX from "xlsx";
import { CircularProgress } from "@mui/material";
import api from "../../util/axios";

type Refund = {
  _id: string;
  businessRef: string;
  branchId: string;
  customerAccount: {
    customerId: {
      businessName: string;
    }
  };
  details: string;
  staff: {
    _id: string;
    name: string;
    role: string;
  };
  amount: number;
  createdAt: string;
  updatedAt: string;
};

const RefundReport = () => {
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(1); // Display one day per page
  const [page, setPage] = useState(0);
  const [reLogin, setReLogin] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const generatePDF = () => {
    const slipElement = document.querySelector(".invoiceTable");

    if (slipElement) {
      const pdfOptions = {
        margin: 2,
        filename: "Refund-Report.pdf",
        image: { type: "png" },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      };
      html2pdf().from(slipElement).set(pdfOptions).save();
    }
  };

  const filterRefundsByDate = (refunds: Refund[]) => {
    return refunds.filter((refund) => {
      const refundDate = new Date(refund.createdAt);
      const currentDate = new Date();

      const startDate = fromDate ? new Date(fromDate) : null;
      const endDate = toDate ? new Date(toDate) : null;
      if (endDate) {
        endDate.setHours(currentDate.getHours());
        endDate.setMinutes(currentDate.getMinutes());
        endDate.setSeconds(currentDate.getSeconds());
      }

      if (startDate && endDate) {
        return refundDate >= startDate && refundDate <= endDate;
      } else if (startDate) {
        return refundDate >= startDate;
      } else if (endDate) {
        return refundDate <= endDate;
      } else {
        return true; // No date range selected, so don't filter
      }
    });
  };

  const groupRefundsByDay = (refunds: Refund[]) => {
    const grouped = refunds.reduce((acc, refund) => {
      const date = new Date(refund.createdAt).toLocaleDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(refund);
      return acc;
    }, {} as Record<string, Refund[]>);
    return grouped;
  };

  const token = window.localStorage.getItem("token");

  const queryRefunds = () => {
    setIsLoading(true);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return api
      .get(`/customer/refund`, {
        headers: headers,
      })
      .then((res) => {
        setRefunds(res.data.data);
      })
      .catch((error) => {
        if (
          error.response.data.message === "jwt expired" ||
          error.response.data.message === "jwt malformed"
        ) {
          setReLogin(true);
        } else {
          setReLogin(false);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    queryRefunds();
  }, []);

  const [searchQuery, setSearchQuery] = useState("");

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = () => {
    const filteredData = filterRefundsByDate(refunds)?.map((refund, index) => {
      const createdAtDate = new Date(refund.createdAt);
      const formattedDate = createdAtDate.toLocaleString();
      return {
        "S/N": index + 1,
        "Business Ref": refund.customerAccount.customerId.businessName,
        Details: refund.details,
        Amount: refund.amount,
        "Created By": refund.staff.name,
        Date: formattedDate,
      };
    });

    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = [
      { width: 5 },
      { width: 15 },
      { width: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RefundReports");

    XLSX.writeFile(wb, "Refund-Report.xlsx");
  };

  const groupedRefunds = groupRefundsByDay(filterRefundsByDate(refunds));
  const days = Object.keys(groupedRefunds).sort((a, b) => {
    if (a === new Date().toLocaleDateString()) return -1;
    if (b === new Date().toLocaleDateString()) return 1;
    return new Date(b).getTime() - new Date(a).getTime(); // Reverse order
  });

  return (
    <div className="board">
      <div className="method" style={{ marginTop: 0 }}>
        <div className="apInput">
          <input
            type="text"
            value={searchQuery}
            placeholder="Search"
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <BsSearch className="apIcon" />
        </div>
        <div className="filterBy">
          Filter By:
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>

      {reLogin ? <ReLoginModal /> : null}
      <div className="invoiceTable">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2>Please wait while we process the request...</h2>
          </div>
        ) : days.length > 0 ? (
          <div>
            <h3 className="dateModal">Date: {days[page]}</h3>
            <table className="inTable">
              <thead>
                <tr className="heTable">
                  <th>S/N</th>
                  <th>Customer Name</th>
                  <th>Details</th>
                  <th>Amount</th>
                  <th>Created By</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {groupedRefunds[days[page]]
                  .filter((refund) =>
                    refund.customerAccount.customerId.businessName
                      .toLowerCase()
                      .includes(searchQuery.toLowerCase())
                  )
                  .map((refund, index) => {
                    const createdAtDate = new Date(refund.createdAt);
                    const formattedDate = createdAtDate.toLocaleString();
                    return (
                      <tr key={refund._id} className="taData">
                        <td>{index + 1}</td>
                        <td>{refund.customerAccount.customerId.businessName}</td>
                        <td>{refund.details}</td>
                        <td>{refund.amount}</td>
                        <td>{refund.staff.name}</td>
                        <td>{formattedDate}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No refunds available for the selected date range.</p>
        )}
      </div>

      <TablePagination
        rowsPerPageOptions={[1]}
        component="div"
        count={days.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <div className="dowBtn">
        <button onClick={generatePDF}>Print Report</button>
        <button onClick={exportToExcel}>Export</button>
      </div>
    </div>
  );
};

export default RefundReport;

